import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User } from 'lucide-react';

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!termsAccepted) {
      toast.error('You must accept the Terms & Conditions');
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password, termsAccepted);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B]" data-testid="signup-page">
      <Header />
      
      <main className="pt-24 px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 animate-fade-in-up">
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter leading-none uppercase text-white mb-2">
              Join TRAINR
            </h1>
            <p className="text-gray-400">Start your transformation today</p>
          </div>

          <div className="bg-[#141414] rounded-2xl p-6 sm:p-8 border border-white/5 animate-fade-in-up animation-delay-200">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-400">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-12 h-14 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-400">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-14 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-400">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 h-14 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                    data-testid="password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-400">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-12 h-14 bg-[#1A1A1A] border-transparent text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent rounded-xl"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Terms & Conditions Checkbox */}
              <div className="flex items-start space-x-3 p-4 bg-[#1A1A1A] rounded-xl border border-white/5">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5 border-gray-600 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  data-testid="terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-gray-400 leading-relaxed cursor-pointer">
                  I have read and agree to the{' '}
                  <Link to="/terms" className="text-primary hover:underline" target="_blank">
                    Terms & Conditions
                  </Link>
                  , including the refund policy (50% refund for user no-shows) and liability limitations.
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-primary text-white font-bold uppercase tracking-wider rounded-xl hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
                data-testid="signup-submit"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
